import { createContext, useContext, forwardRef, useRef, Children, useEffect, cloneElement, useState, useCallback } from 'react';
import cn from 'classnames';
import Button from '@ebsco-ui/button';
import { createBemBlockBuilder } from '@ebsco-ui/utils';
import { jsx, Fragment } from 'react/jsx-runtime';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

var ShowMoreContext = createContext(undefined);

var useShowMoreContext = function useShowMoreContext() {
  var context = useContext(ShowMoreContext);

  if (context === undefined) {
    throw new Error("ShowMore compound components cannot be rendered outside the ShowMore component");
  }

  return context;
};

var ShowMoreContextComponent = function ShowMoreContextComponent(_ref) {
  var children = _ref.children;
  var context = useShowMoreContext();
  return children(context);
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var _excluded = ["expandContent", "collapseContent", "styleType", "className"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var ShowMoreButton = forwardRef(function (_ref, forwardedRef) {
  var expandContent = _ref.expandContent,
      collapseContent = _ref.collapseContent,
      _ref$styleType = _ref.styleType,
      styleType = _ref$styleType === void 0 ? "inline" : _ref$styleType,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useShowMoreContext = useShowMoreContext(),
      isExpanded = _useShowMoreContext.isExpanded,
      toggleIsExpanded = _useShowMoreContext.toggleIsExpanded,
      totalItems = _useShowMoreContext.totalItems,
      limit = _useShowMoreContext.limit;

  if (totalItems <= limit) {
    return null;
  }

  var getClassNames = function getClassNames() {
    var getBlocksWith = createBemBlockBuilder(["eb-show-more-button", className]);
    return cn(getBlocksWith(), isExpanded ? getBlocksWith("--expanded") : getBlocksWith("--collapsed"));
  };

  return /*#__PURE__*/jsx(Button, _objectSpread$1(_objectSpread$1({
    ref: forwardedRef,
    className: getClassNames(),
    onClick: toggleIsExpanded,
    styleType: styleType,
    type: "button"
  }, rest), {}, {
    children: isExpanded ? collapseContent : expandContent
  }));
});
ShowMoreButton.displayName = "ShowMoreButton";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var ShowMoreItems = function ShowMoreItems(_ref) {
  var children = _ref.children;
  var showMoreItemRef = useRef(null);

  var _useShowMoreContext = useShowMoreContext(),
      isExpanded = _useShowMoreContext.isExpanded,
      limit = _useShowMoreContext.limit,
      setTotalItems = _useShowMoreContext.setTotalItems;

  var allItems = Children.toArray(children);
  var itemsToRender = allItems.slice(0, isExpanded ? allItems.length : limit);
  var numberOfChildren = Children.count(children);
  useEffect(function () {
    setTotalItems(numberOfChildren);
  }, [numberOfChildren, setTotalItems]);
  useEffect(function () {
    if (showMoreItemRef.current) {
      showMoreItemRef.current.focus();
    }

    if (isExpanded && !showMoreItemRef.current) {
      console.error("The ShowMore component provides a ref and tabindex to the components passed as items. Please make sure that these components can accept refs and tabindex as props.");
    }
  }, [isExpanded, showMoreItemRef]);

  var getClassNames = function getClassNames(classNames) {
    var getBlocksWith = createBemBlockBuilder(classNames);
    return cn(getBlocksWith(), getBlocksWith(isExpanded ? "--expanded" : "--collapsed"));
  };

  var visibleItems = itemsToRender.map(function (child, index) {
    var baseProps = _objectSpread(_objectSpread({}, child.props), {}, {
      className: getClassNames([child.props.className, "eb-show-more-item"]),
      key: index
    });

    var focusableProps;

    if (index === limit) {
      focusableProps = {
        ref: showMoreItemRef,
        tabIndex: -1
      };
    }

    return cloneElement(child, _objectSpread(_objectSpread({}, baseProps), focusableProps));
  });
  return /*#__PURE__*/jsx(Fragment, {
    children: visibleItems
  });
};

var ShowMore = function ShowMore(_ref) {
  var _ref$limit = _ref.limit,
      limit = _ref$limit === void 0 ? 10 : _ref$limit,
      children = _ref.children,
      className = _ref.className;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isExpanded = _useState2[0],
      setIsExpanded = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      totalItems = _useState4[0],
      setTotalItems = _useState4[1];

  var setItems = useCallback(function (newState) {
    setTotalItems(newState);
  }, [setTotalItems]);
  return /*#__PURE__*/jsx(ShowMoreContext.Provider, {
    value: {
      limit: limit,
      isExpanded: isExpanded,
      toggleIsExpanded: function toggleIsExpanded() {
        return setIsExpanded(!isExpanded);
      },
      totalItems: totalItems,
      setTotalItems: setItems
    },
    children: /*#__PURE__*/jsx("div", {
      className: cn("eb-show-more", className),
      "data-auto": "show-more",
      children: children
    })
  });
};

ShowMore.Context = ShowMoreContextComponent;
ShowMore.Button = ShowMoreButton;
ShowMore.Items = ShowMoreItems;

export { ShowMore as default };
//# sourceMappingURL=show-more-esm.js.map
